import type { Launch } from "@models/launch.model";
import type { PopulationSegment } from "@models/populationSegment.model";
import { formatStartDate } from "../monitor/dateFilter";

export type ObjectiveOption = { id: number; name: string };

/*
    A campaign belongs to an objective, so the list is grouped by objective the
    way the Ad Groups step is grouped by campaign — each objective creates its
    own campaigns, which is what ties the two together at creation time.

    Campaigns with no objective (or one that is no longer in the engagement)
    still have a home: the group label falls back to a plain string.
*/
export const NO_OBJECTIVE = "No objective";

/** The campaign's own dates as one cell. Em dash between, "Not set" where there are none —
 *  114 of the 200 campaigns in production have no dates yet (MPN-2040). */
function formatDateRange(start?: string | null, end?: string | null): string {
  if (!start || !end) return "Not set";
  const day = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${day(start)} – ${day(end)}`;
}

/*
    An objective is a record when the engagement has one, and otherwise just a
    string — a campaign is never dropped for want of a real objective.
*/
export function objectiveNameFor(
  campaign: { objective_id?: number },
  objectives: ObjectiveOption[],
): string {
  const objectiveId = campaign.objective_id;
  if (objectiveId == null) return NO_OBJECTIVE;
  return objectives.find((o) => o.id === objectiveId)?.name ?? `Objective #${objectiveId}`;
}

type CampaignGroup = {
  key: string;
  objectiveId: number | null;
  objectiveName: string;
  campaigns: Launch[];
};

function groupByObjective(campaigns: Launch[], objectives: ObjectiveOption[]): CampaignGroup[] {
  const groups: CampaignGroup[] = objectives.map((objective) => ({
    key: `objective-${objective.id}`,
    objectiveId: objective.id,
    objectiveName: objective.name,
    campaigns: [],
  }));
  const byObjectiveId = new Map<number | null, CampaignGroup>(
    groups.map((group) => [group.objectiveId, group]),
  );

  for (const campaign of campaigns) {
    const objectiveId = campaign.objective_id ?? null;
    let group = byObjectiveId.get(objectiveId);
    if (!group) {
      group = {
        key: objectiveId == null ? "objective-none" : `objective-${objectiveId}`,
        objectiveId,
        objectiveName: objectiveNameFor(campaign, objectives),
        campaigns: [],
      };
      byObjectiveId.set(objectiveId, group);
      groups.push(group);
    }
    group.campaigns.push(campaign);
  }

  return groups;
}

type Props = {
  campaigns: Launch[];
  objectives: ObjectiveOption[];
  segments: PopulationSegment[];
  livePublisherCountById: Record<number, number>;
  publisherNetworksById: Record<number, string[]>;
  networkLabelByKey: Record<string, string>;
  isBusy: boolean;
  onOpen: (campaignId: number) => void;
  onOpenObjective: (objectiveId: number) => void;
  onCreate: (objective: { id: number | null; name: string }) => void;
  onCreateObjective: () => void;
};

// Cap the subline at two publisher names plus a "+N more" so a campaign with
// many live publishers doesn't blow out the row height.
const MAX_PUBLISHER_NAMES = 2;

export default function CampaignList({
  campaigns,
  objectives,
  segments,
  livePublisherCountById,
  publisherNetworksById,
  networkLabelByKey,
  isBusy,
  onOpen,
  onOpenObjective,
  onCreate,
  onCreateObjective,
}: Props) {
  const publisherNames = (campaign: Launch) =>
    (publisherNetworksById[campaign.id] ?? []).map(
      (network) => networkLabelByKey[network.toLowerCase()] ?? network,
    );

  const subline = (campaign: Launch) => {
    const org = campaign.organizations?.[0]?.name ?? "No sub-org";
    const segment =
      segments.find((s) => s.id === campaign.population_segment_id)?.name ?? "No segment";
    const names = publisherNames(campaign);
    const publishers = names.length
      ? names.length > MAX_PUBLISHER_NAMES
        ? `${names.slice(0, MAX_PUBLISHER_NAMES).join(", ")} +${names.length - MAX_PUBLISHER_NAMES} more`
        : names.join(", ")
      : "No publishers";
    return `${org} · ${segment} · ${publishers}`;
  };

  // Nothing to group by and nothing grouped: one prompt, not an empty scaffold.
  if (!campaigns.length && !objectives.length) {
    return (
      <div className="campaign-empty">
        <div className="campaign-empty__title">No campaigns yet</div>
        <div className="campaign-empty__body">
          Campaigns are grouped under the objective they serve. Start with an objective, or create a
          campaign now and tie it to one later.
        </div>
        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onCreateObjective}
            disabled={isBusy}
          >
            + Objective
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => onCreate({ id: null, name: NO_OBJECTIVE })}
            disabled={isBusy}
          >
            + Campaign
          </button>
        </div>
      </div>
    );
  }

  const groups = groupByObjective(campaigns, objectives);

  return (
    <div className="campaign-pane campaign-pane--list">
      <div className="campaign-list__head">
        <span className="campaign-list__title">Campaigns</span>
        <span className="campaign-list__note">
          {campaigns.length} in this engagement, grouped by objective
        </span>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm ms-auto"
          style={{ fontSize: "0.72rem" }}
          onClick={onCreateObjective}
          disabled={isBusy}
        >
          + Objective
        </button>
      </div>

      {groups.map((group) => (
        <div key={group.key} className="campaign-section">
          <div className="campaign-section__head">
            {/* The objective owns list sizing and key dates, so its title is the
                way in. Campaigns with no objective have nothing to open. */}
            {group.objectiveId == null ? (
              <span className="campaign-section__title">{group.objectiveName}</span>
            ) : (
              <button
                type="button"
                className="campaign-section__title campaign-section__title--link"
                onClick={() => onOpenObjective(group.objectiveId as number)}
                disabled={isBusy}
                title="Open this objective"
              >
                {group.objectiveName}
                <i className="bi bi-chevron-right" />
              </button>
            )}
            <span className="campaign-section__count">{group.campaigns.length}</span>
            <button
              type="button"
              className="btn btn-primary btn-sm ms-auto"
              style={{ fontSize: "0.72rem" }}
              onClick={() => onCreate({ id: group.objectiveId, name: group.objectiveName })}
              disabled={isBusy}
            >
              + Campaign
            </button>
          </div>

          <div className="campaign-section__body">
            {group.campaigns.length === 0 ? (
              <div className="campaign-section__empty">No campaigns yet</div>
            ) : (
              <div className="campaign-card">
                {group.campaigns.map((campaign) => {
                  const live = livePublisherCountById[campaign.id] ?? 0;
                  // Readiness on the backend requires publishers AND a population
                  // segment; a green pill here while the segment is missing would
                  // disagree with the sidebar reporting the item incomplete.
                  const needsSegment = live > 0 && campaign.population_segment_id == null;
                  const pillLabel = needsSegment
                    ? "Needs segment"
                    : live
                      ? `${live} publisher${live === 1 ? "" : "s"} live`
                      : "Not configured";
                  return (
                    <button
                      key={campaign.id}
                      type="button"
                      className="campaign-row"
                      onClick={() => onOpen(campaign.id)}
                      disabled={isBusy}
                    >
                      <span className="campaign-row__main">
                        <span className="campaign-row__name">{campaign.name}</span>
                        <span className="campaign-row__sub">{subline(campaign)}</span>
                      </span>
                      {/*
                          Two columns, not one (MPN-2040). This was a single "Campaign Start
                          Date" whose VALUE was `go_live_date` -- the ambiguity that ticket
                          exists to remove, since the campaign's own start and the day it
                          first reached a network are different facts and disagree on 21 of
                          the 57 published campaigns in production, three by over 190 days.

                          Still columns rather than clauses on the subline, for the reason
                          the original gave: a date buried in a sentence is not scannable
                          down a list.
                      */}
                      <span className="campaign-row__start">
                        <span className="campaign-row__start-label">Campaign dates</span>
                        <span className="campaign-row__start-value">
                          {formatDateRange(campaign.campaign_start, campaign.campaign_end)}
                        </span>
                      </span>
                      <span className="campaign-row__start">
                        <span className="campaign-row__start-label">Went live</span>
                        <span className="campaign-row__start-value">
                          {formatStartDate(campaign.go_live_date)}
                        </span>
                      </span>
                      <span
                        className={`campaign-pill ${live && !needsSegment ? "campaign-pill--live" : "campaign-pill--warn"}`}
                      >
                        {pillLabel}
                      </span>
                      <span className="campaign-row__chev">›</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
